import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  EnvelopeSimple, FacebookLogo, InstagramLogo, TiktokLogo, CreditCard,
  ArrowClockwise, CircleNotch, CheckCircle, WarningCircle, ArrowSquareOut, Copy,
} from "@phosphor-icons/react";

const ICONS = {
  envelope: EnvelopeSimple, facebook: FacebookLogo, instagram: InstagramLogo,
  tiktok: TiktokLogo, stripe: CreditCard,
};

export default function ConnectionsHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/connections/health");
      setData(data);
    } catch {
      setData(null);
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="connections-health" className="rounded-2xl border border-white/10 bg-zinc-900/60 backdrop-blur p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-tight">Santé des connexions</span>
          {data && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/10" data-testid="connections-count">
              {data.connected}/{data.total} actives
            </span>
          )}
        </div>
        <button onClick={load} data-testid="connections-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors">
          <ArrowClockwise className={loading ? "animate-spin" : ""} size={14} /> Actualiser
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-6 text-zinc-500"><CircleNotch size={20} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
          {(data?.services || []).map((s) => {
            const Icon = ICONS[s.icon] || EnvelopeSimple;
            const ok = s.connected;
            return (
              <div key={s.key} data-testid={`connection-${s.key}`}
                className={`relative rounded-xl border p-3 flex flex-col gap-1.5 ${ok ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`}>
                <div className="flex items-center gap-2">
                  <Icon size={18} weight="fill" className={ok ? "text-emerald-300" : "text-red-300"} />
                  <span className="text-sm font-semibold flex-1">{s.label}</span>
                  {ok ? <CheckCircle size={16} weight="fill" className="text-emerald-400" data-testid={`status-ok-${s.key}`} />
                      : <WarningCircle size={16} weight="fill" className="text-red-400" data-testid={`status-down-${s.key}`} />}
                </div>
                {ok && s.key === "tiktok" && (s.avatar_url || s.follower_count != null) ? (
                  <div className="flex items-center gap-2 mt-0.5" data-testid="tiktok-preview">
                    {s.avatar_url && <img src={s.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />}
                    <div className="min-w-0">
                      <div className="text-[12px] font-semibold text-emerald-200 truncate">{s.detail}</div>
                      {s.follower_count != null && (
                        <div className="text-[10px] text-emerald-300/70">{Number(s.follower_count).toLocaleString("fr-CA")} abonnés</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className={`text-[11px] truncate ${ok ? "text-emerald-200/80" : "text-red-200/80"}`} title={s.detail}>{s.detail}</span>
                )}
                {!ok && s.redirect_uri && (
                  <div className="mt-1 rounded-lg bg-black/30 border border-white/10 p-1.5" data-testid={`redirect-uri-${s.key}`}>
                    <div className="text-[9px] uppercase tracking-wide text-zinc-500 mb-0.5">URI à enregistrer (exacte)</div>
                    <div className="flex items-center gap-1">
                      <code className="text-[10px] text-amber-200 truncate flex-1" title={s.redirect_uri}>{s.redirect_uri}</code>
                      <button onClick={() => { navigator.clipboard.writeText(s.redirect_uri); toast.success("URI copiée !"); }}
                        data-testid={`copy-uri-${s.key}`} className="p-1 rounded hover:bg-white/10 text-zinc-400 hover:text-white shrink-0" title="Copier l'URI">
                        <Copy size={12} />
                      </button>
                    </div>
                  </div>
                )}
                {!ok && (
                  <div className="flex gap-1.5 mt-1">
                    <Link to={s.reconnect_path} data-testid={`reconnect-${s.key}`}
                      className="flex-1 inline-flex items-center justify-center text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 transition-colors">
                      Reconnecter
                    </Link>
                    {s.console_url && (
                      <a href={s.console_url} target="_blank" rel="noopener noreferrer" data-testid={`console-${s.key}`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 transition-colors whitespace-nowrap"
                        title={`Ouvrir ${s.console_label}`}>
                        <ArrowSquareOut size={12} /> {s.console_label}
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
